import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import { unref, toRaw } from 'vue'
import { defineStore } from 'pinia'
import { store } from '../index'
import { enableStoreHMRUpdate } from '../helper'
import { useGo, useRedo } from '@/composables/usePage'
import { PageEnum } from '@/enums/pageEnum'
import { pageNotFoundRoute, redirectRoute } from '@/router/basic'
import { storageProxy } from '@/utils/cache'
import { CacheKeys } from '@/enums/cacheEnum'
import { useDetailIframe } from '@/layouts/iframe/useDetailIframe'

export type TagView = Omit<
  RouteLocationNormalized,
  'query' | 'matched' | 'params' | 'redirectedFrom' | 'hash'
> & {
  title?: string
  query?: RouteLocationNormalized['query']
  matched?: RouteLocationNormalized['matched']
  params?: RouteLocationNormalized['params']
  redirectedFrom?: RouteLocationNormalized['redirectedFrom']
}

export interface State {
  visitedViews: TagView[]
  cachedViews: Set<string>
  lastDragEndIndex: number
}

const handleGotoPage = (router: Router) => {
  const go = useGo(router)
  go(unref(router.currentRoute).fullPath, true)
}

const getToTarget = (tagView: TagView) => {
  const { params, path, query } = tagView
  return {
    params: params || {},
    path,
    query: query || {},
  }
}

export const useTagsViewStore = defineStore('tagsViewStore', {
  state: (): State => ({
    // 已打开路由列表
    // visitedViews: storageProxy.get(CacheKeys.TAGS_VIEW) || [],
    visitedViews: [],
    // 需缓存的路由列表
    cachedViews: new Set(),
    // 记录上一次拖拽tag的索引，每次拖动自增
    lastDragEndIndex: 0,
  }),
  getters: {
    getVisitedViews(): TagView[] {
      return this.visitedViews
    },
    getCachedViews(): string[] {
      return [...this.cachedViews]
    },
  },
  actions: {
    /**
     * 根据已打开的路由更新缓存列表
     */
    async updateCacheViews() {
      const cacheMap: Set<string> = new Set()

      for (const views of this.visitedViews) {
        const needCache = !views.meta?.ignoreKeepAlive
        if (!needCache) {
          continue
        }
        const name = views.name as string
        name && cacheMap.add(name)
      }
      this.cachedViews = cacheMap
      storageProxy.set(CacheKeys.TAGS_VIEW, this.visitedViews)
    },

    /**
     * 刷新页面当前路由
     * @param router
     */
    async refreshPage(router: Router) {
      const { currentRoute } = router
      const route = unref(currentRoute)
      const name = route.name

      const findView = this.getCachedViews.find((item) => item === name)
      if (findView) {
        this.cachedViews.delete(findView)
      }
      const redo = useRedo(router)
      await redo()
      if (route.meta.frameSrc) {
        const targetIframeEl = document.querySelector(
          `iframe[src$="${route.meta.frameSrc}"]`,
        ) as HTMLIFrameElement
        if (targetIframeEl) {
          targetIframeEl.contentWindow?.location.reload()
        }
      }
    },
    // 清空路由缓存
    clearCacheTags(): void {
      this.cachedViews = new Set()
      storageProxy.remove(CacheKeys.TAGS_VIEW)
    },
    // 重置状态
    resetState(): void {
      this.visitedViews = []
      this.clearCacheTags()
      this.lastDragEndIndex = 0
    },
    /**
     * 跳转页面，如果当前打开的页面不是首页，就跳转到首页
     * @param router
     */
    goToPage(router: Router) {
      const go = useGo(router)
      const len = this.visitedViews.length
      const { path } = unref(router.currentRoute)

      let toPath: PageEnum | string = PageEnum.BASE_HOME

      if (len > 0) {
        const page = this.visitedViews[len - 1]
        const p = page.fullPath || page.path
        if (p) {
          toPath = p
        }
      }
      path !== toPath && go(toPath, true)
    },
    /**
     * 添加tagView， 如果已打开的路由列表中存在该路由，则进行参数替换，否则添加到列表中
     * @param route
     * @returns
     */
    async addTagView(route: TagView) {
      const { path, name, fullPath, params, query, meta } = route
      // 如果是404，登录页，没有name的页面不加入tagsview中
      if (
        path === PageEnum.BASE_LOGIN ||
        path === PageEnum.BASE_HOME ||
        !name ||
        [redirectRoute.name, pageNotFoundRoute.name].includes(name)
      ) {
        return
      }

      let updateIndex = -1
      const tagViewHasExits = this.visitedViews.some((tagView, index) => {
        updateIndex = index
        // return tagView.path === path || tagView.fullPath === fullPath
        return tagView.fullPath === fullPath || decodeURIComponent(tagView.fullPath) === fullPath
      })

      if (tagViewHasExits) {
        const curTagView = toRaw(this.visitedViews)[updateIndex]
        if (!curTagView) {
          return
        }
        curTagView.params = params || curTagView.params
        curTagView.query = query || curTagView.query
        curTagView.fullPath = fullPath || curTagView.fullPath
        this.visitedViews.splice(updateIndex, 1, curTagView)
      } else {
        const dynamicLevel = meta?.dynamicLevel ?? -1
        if (dynamicLevel > 0) {
          const realPath = meta?.realPath ?? ''
          if (
            this.visitedViews.filter((e) => e.meta?.realPath ?? '' === realPath).length >=
            dynamicLevel
          ) {
            // 关闭第一个
            const index = this.visitedViews.findIndex((item) => item.meta.realPath === realPath)
            index !== -1 && this.visitedViews.splice(index, 1)
          }
        }
        this.visitedViews.push(route)
      }
      this.updateCacheViews()
    },
    /**
     * 关闭tag标签，如果该标签不是当前页面，则直接关闭。否则，关闭该标签后，打开相邻的标签。
     * 规则是：如果在最左边，且还有其他标签，则打开下一个，如没有，则打开首页；如果不在最左边，则打开上一个
     * @param tab
     * @param router
     * @returns
     */
    async closeTagView(tab: TagView, router: Router) {
      const { removeDetailIframe } = useDetailIframe()
      const close = (route: TagView) => {
        const { fullPath, meta: { affix } = {} } = route
        if (affix) {
          return
        }
        const index = this.visitedViews.findIndex((item) => item.fullPath === fullPath)
        index !== -1 && this.visitedViews.splice(index, 1)
        this.updateCacheViews()
        removeDetailIframe(fullPath)
      }

      const { currentRoute, replace } = router

      const { path, fullPath } = unref(currentRoute)
      if (tab.fullPath !== fullPath || path !== tab.path) {
        close(tab)
        return
      }

      let toTarget: RouteLocationRaw = {}

      const index = this.visitedViews.findIndex((item) => item.fullPath === fullPath)

      // 如果关闭的在最左侧
      if (index === 0) {
        // 只有一个打开的路由
        if (this.visitedViews.length === 1) {
          toTarget = PageEnum.BASE_HOME
        } else {
          const page = this.visitedViews[index + 1]
          toTarget = getToTarget(page)
        }
      } else {
        const page = this.visitedViews[index - 1]
        toTarget = getToTarget(page)
      }
      close(currentRoute.value)
      await replace(toTarget)
    },
    /**
     * 关闭tag标签根据path
     * @param key
     * @param router
     */
    async closeTagViewByKey(key: string, router: Router) {
      const index = this.visitedViews.findIndex((item) => (item.fullPath || item.path) === key)
      if (index !== -1) {
        await this.closeTagView(this.visitedViews[index], router)
      }
    },

    /**
     * 排序tag标签
     * @param oldIndex
     * @param newIndex
     */
    async sortTagViews(oldIndex: number, newIndex: number) {
      const currentTagView = this.visitedViews[oldIndex]
      this.visitedViews.splice(oldIndex, 1)
      this.visitedViews.splice(newIndex, 0, currentTagView)
      this.lastDragEndIndex = this.lastDragEndIndex + 1
    },

    /**
     * 关闭左边所有Tag
     * @param route
     * @param router
     */
    async closeLeftTags(route: TagView, router: Router) {
      const index = this.visitedViews.findIndex((item) => item.fullPath === route.fullPath)

      if (index > 0) {
        const leftTags = this.visitedViews.slice(0, index)
        const pathList: string[] = []
        for (const item of leftTags) {
          const affix = item?.meta?.affix ?? false
          if (!affix) {
            pathList.push(item.fullPath)
          }
        }
        this.bulkCloseTags(pathList)
      }
      this.updateCacheViews()
      handleGotoPage(router)
    },

    /**
     * 关闭右边所有tag
     * @param route
     * @param router
     */
    async closeRightTags(route: TagView, router: Router) {
      const index = this.visitedViews.findIndex((item) => item.fullPath === route.fullPath)

      if (index >= 0 && index < this.visitedViews.length - 1) {
        const rightTags = this.visitedViews.slice(index + 1, this.visitedViews.length)

        const pathList: string[] = []
        for (const item of rightTags) {
          const affix = item?.meta?.affix ?? false
          if (!affix) {
            pathList.push(item.fullPath)
          }
        }
        this.bulkCloseTags(pathList)
      }
      this.updateCacheViews()
      handleGotoPage(router)
    },

    /**
     * 关闭所有标签
     * @param router
     */
    async closeAllTag(router: Router) {
      this.visitedViews = this.visitedViews.filter((item) => item?.meta?.affix ?? false)
      this.updateCacheViews()
      this.clearCacheTags()
      this.goToPage(router)
    },

    /**
     * 关闭其他标签
     * @param route
     * @param router
     */
    async closeOtherTags(route: TagView, router: Router) {
      const closePathList = this.visitedViews.map((item) => item.fullPath)

      const pathList: string[] = []

      for (const path of closePathList) {
        if (path !== route.fullPath) {
          const closeItem = this.visitedViews.find((item) => item.fullPath === path)
          if (!closeItem) {
            continue
          }
          const affix = closeItem?.meta?.affix ?? false
          if (!affix) {
            pathList.push(closeItem.fullPath)
          }
        }
      }
      this.bulkCloseTags(pathList)
      this.updateCacheViews()
      handleGotoPage(router)
    },

    /**
     * 批量关闭Tag
     * @param pathList
     */
    async bulkCloseTags(pathList: string[]) {
      const { removeDetailIframe } = useDetailIframe()
      this.visitedViews = this.visitedViews.filter((item) => !pathList.includes(item.fullPath))
      pathList.forEach((fullPath) => removeDetailIframe(fullPath))
    },

    // 设置标签名字
    async setTagTitle(title: string, route: TagView) {
      const findTag = this.getVisitedViews.find((item) => item === route)
      if (findTag) {
        findTag.meta.title = title
        await this.updateCacheViews()
      }
    },
    // 更新标签地址
    async updateTagPath(fullPath: string, route: TagView) {
      const findTag = this.getVisitedViews.find((item) => item === route)
      if (findTag) {
        findTag.fullPath = fullPath
        findTag.path = fullPath
        await this.updateCacheViews()
      }
    },
  },
})

enableStoreHMRUpdate(useTagsViewStore)

export function useTagsViewStoreWithOut() {
  return useTagsViewStore(store)
}
